import React, { useEffect, useRef } from "react";
import graphicDesign from "../images/graphic-design.svg";
import gaming from "../images/gaming.svg";
import mobileDesktop from "../images/mobile-and-desktop.svg";
import tutoring from "../images/tutoring.svg";
import others from "../images/others.svg";
import webDesign from "../images/web-design.svg";
import { 
  FaPencilRuler, 
  FaGamepad, 
  FaMobile, 
  FaChalkboardTeacher, 
  FaCode, 
  FaDesktop,
  FaAngleRight
} from "react-icons/fa";
import Aos from "aos";
import "aos/dist/aos.css";
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import "./Services.css";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function Services() {
  const heroSectionRef = useRef(null);
  const particlesRef = useRef(null);
  const ctaSectionRef = useRef(null);
  const serviceCardsRef = useRef(null);
  
  useEffect(() => {
    Aos.init({
      duration: 1000,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1.0)',
      once: false,
    });
    
    // Initialize GSAP animations
    initAnimations();
    
    // Initialize particle animations
    initParticleAnimations();
    
    return () => {
      // Clean up ScrollTrigger instances
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);
  
  const initAnimations = () => {
    // Hero section animations
    if (heroSectionRef.current) {
      const heroTitle = heroSectionRef.current.querySelector('h1');
      const heroBadge = heroSectionRef.current.querySelector('.services-badge');
      const heroParagraph = heroSectionRef.current.querySelector('p');
      
      gsap.fromTo(heroBadge, 
        { opacity: 0, y: 30 }, 
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
      );
      
      gsap.fromTo(heroTitle, 
        { opacity: 0, y: 40 }, 
        { opacity: 1, y: 0, duration: 1, delay: 0.2, ease: 'power3.out' }
      );
      
      gsap.fromTo(heroParagraph, 
        { opacity: 0, y: 40 }, 
        { opacity: 1, y: 0, duration: 1, delay: 0.4, ease: 'power3.out' }
      );
      
      // Animate SVG elements
      gsap.fromTo(
        heroSectionRef.current.querySelectorAll('.animated-svg'),
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1, stagger: 0.2, ease: 'power2.out' }
      );
    }
    
    // Service cards animations
    if (serviceCardsRef.current) {
      const serviceItems = serviceCardsRef.current.querySelectorAll('.service-item');
      
      serviceItems.forEach((item, index) => {
        gsap.fromTo(
          item,
          { y: 50, opacity: 0 },
          { 
            y: 0, 
            opacity: 1, 
            duration: 0.8, 
            delay: 0.1 * index,
            scrollTrigger: {
              trigger: item,
              start: 'top 85%',
            }
          }
        );
      });
    }
    
    // CTA section animations
    if (ctaSectionRef.current) {
      gsap.fromTo(
        ctaSectionRef.current.querySelector('.services-cta-content'),
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: ctaSectionRef.current,
            start: 'top 75%',
          }
        }
      );
      
      // Animate CTA buttons
      gsap.fromTo(
        ctaSectionRef.current.querySelectorAll('.cta-button'),
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.2,
          delay: 0.3,
          scrollTrigger: {
            trigger: ctaSectionRef.current,
            start: 'top 75%',
          }
        }
      );
    }
  };
  
  const initParticleAnimations = () => {
    // Animate floating particles
    if (particlesRef.current) {
      const particles = particlesRef.current.querySelectorAll('.particle');
      particles.forEach(particle => {
        gsap.to(particle, {
          x: "random(-30, 30)",
          y: "random(-30, 30)",
          duration: "random(3, 8)",
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut"
        });
      });
    }
  };

  return (
    <div className="services-container">
      {/* Services Hero Section */}
      <section className="services-hero" ref={heroSectionRef}>
        <div className="services-hero-content container">
          <div className="services-badge" data-aos="fade-up">Our Services</div>
          <h1 data-aos="fade-up" data-aos-delay="100">Innovative Digital <span className="text-highlight">Solutions</span></h1>
          <p data-aos="fade-up" data-aos-delay="200">
            We provide a wide range of professional services to help businesses 
            and individuals succeed in the digital world.
          </p>
        </div>
        
        {/* Enhanced Animated SVGs for Hero Section */}
        <div className="animated-svg-container">
          <svg className="animated-svg svg-1" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#2c3c54" opacity="0.15" d="M47.5,-61.2C59.9,-53.9,67.4,-38.1,72.9,-21.4C78.4,-4.8,81.9,12.8,76.7,27.3C71.5,41.8,57.5,53.2,42.3,60.2C27.1,67.2,10.7,69.8,-5.1,67.5C-20.9,65.1,-36.1,57.8,-47.8,46.8C-59.6,35.9,-67.9,21.3,-71.1,5C-74.3,-11.3,-72.3,-29.2,-63.3,-41.9C-54.2,-54.5,-38.1,-61.9,-22.9,-67.4C-7.7,-72.9,6.6,-76.5,20.7,-73.4C34.9,-70.3,48.9,-60.5,47.5,-61.2Z" transform="translate(100 100)">
              <animateTransform 
                attributeName="transform" 
                type="translate" 
                values="95 95; 105 95; 105 105; 95 105; 95 95" 
                dur="20s" 
                repeatCount="indefinite" />
              <animate
                attributeName="opacity"
                values="0.15;0.25;0.15"
                dur="8s"
                repeatCount="indefinite" />
            </path>
          </svg>
          
          <svg className="animated-svg svg-2" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#2c3c54" opacity="0.15" d="M53.8,-65.6C68.1,-55.4,77.2,-37.6,79.7,-19.6C82.3,-1.6,78.3,16.5,69.2,30.6C60.1,44.6,46,54.7,30.9,61.1C15.9,67.6,-0.3,70.4,-17.4,68C-34.6,65.7,-52.8,58.1,-65.1,44.3C-77.3,30.4,-83.7,10.2,-80.2,-7.7C-76.7,-25.5,-63.3,-41.1,-47.9,-51.2C-32.6,-61.2,-15.3,-65.7,2.4,-68.6C20.1,-71.5,39.5,-75.8,53.8,-65.6Z" transform="translate(100 100)">
              <animateTransform 
                attributeName="transform" 
                type="translate" 
                values="105 105; 95 105; 95 95; 105 95; 105 105" 
                dur="25s" 
                repeatCount="indefinite" />
              <animate
                attributeName="opacity"
                values="0.15;0.3;0.15"
                dur="10s"
                repeatCount="indefinite" />
            </path>
          </svg>
          
          <svg className="animated-svg svg-3" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#2c3c54" opacity="0.15" d="M40.9,-49.7C53.1,-39.9,63.3,-27.1,67.7,-12.1C72.1,2.9,70.7,20.1,62.6,32.9C54.4,45.7,39.5,54.2,23.6,60.3C7.7,66.4,-9.3,70.2,-25.2,66.4C-41.1,62.5,-56,51.1,-65.8,35.7C-75.5,20.3,-80.2,0.9,-75.4,-15.3C-70.6,-31.5,-56.3,-44.4,-41.3,-53.5C-26.2,-62.5,-10.5,-67.8,2.2,-70.4C14.8,-73,28.7,-59.5,40.9,-49.7Z" transform="translate(100 100)">
              <animateTransform 
                attributeName="transform" 
                type="translate" 
                values="100 100; 110 100; 110 110; 100 110; 100 100" 
                dur="18s" 
                repeatCount="indefinite" />
              <animate
                attributeName="opacity"
                values="0.15;0.25;0.15"
                dur="7s"
                repeatCount="indefinite" />
            </path>
          </svg>
          
          {/* Added new animated elements */}
          <svg className="animated-svg svg-4" width="120" height="120" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke="#942c24" strokeWidth="1" strokeOpacity="0.2">
              <animate 
                attributeName="r" 
                values="50;55;50" 
                dur="5s" 
                repeatCount="indefinite" />
              <animate 
                attributeName="stroke-opacity" 
                values="0.2;0.4;0.2" 
                dur="5s" 
                repeatCount="indefinite" />
            </circle>
            <circle cx="60" cy="60" r="30" fill="none" stroke="#942c24" strokeWidth="1" strokeOpacity="0.3">
              <animate 
                attributeName="r" 
                values="30;35;30" 
                dur="3s" 
                repeatCount="indefinite" />
            </circle>
          </svg>
          
          <svg className="animated-svg svg-5" width="200" height="200" viewBox="0 0 200 200">
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2c3c54" stopOpacity="0.1" />
                <stop offset="100%" stopColor="#942c24" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <path d="M20,50 Q40,20 80,30 T140,50 T180,80 T120,150 T60,130 T20,50" fill="url(#grad1)">
              <animateTransform 
                attributeName="transform" 
                type="rotate"
                from="0 100 100"
                to="360 100 100" 
                dur="40s" 
                repeatCount="indefinite" />
            </path>
          </svg>
          
          {/* New SVG elements */}
          <svg className="animated-svg svg-6" width="150" height="150" viewBox="0 0 150 150">
            <defs>
              <radialGradient id="grad2" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#2c3c54" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#2c3c54" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="75" cy="75" r="70" fill="url(#grad2)">
              <animate 
                attributeName="r" 
                values="70;75;70" 
                dur="8s" 
                repeatCount="indefinite" />
              <animateTransform 
                attributeName="transform" 
                type="rotate"
                from="0 75 75"
                to="360 75 75" 
                dur="60s" 
                repeatCount="indefinite" />
            </circle>
          </svg>
          
          <svg className="animated-svg svg-7" width="100" height="100" viewBox="0 0 100 100">
            <polygon points="50,10 61,35 90,35 65,55 75,80 50,65 25,80 35,55 10,35 39,35" 
              fill="none" stroke="#942c24" strokeWidth="1" strokeOpacity="0.15">
              <animate 
                attributeName="stroke-opacity" 
                values="0.15;0.3;0.15" 
                dur="6s" 
                repeatCount="indefinite" />
              <animateTransform 
                attributeName="transform" 
                type="rotate"
                from="0 50 50"
                to="360 50 50" 
                dur="30s" 
                repeatCount="indefinite" />
            </polygon>
          </svg>
          
          {/* Particles container */}
          <div className="particles-container" ref={particlesRef}>
            <span className="particle particle-1"></span>
            <span className="particle particle-2"></span>
            <span className="particle particle-3"></span>
            <span className="particle particle-4"></span>
            <span className="particle particle-5"></span>
            <span className="particle particle-6"></span>
            <span className="particle particle-7"></span>
            <span className="particle particle-8"></span>
            <span className="particle particle-9"></span>
            <span className="particle particle-10"></span>
            <span className="particle particle-11"></span>
            <span className="particle particle-12"></span>
          </div>
          
          {/* Floating particles */}
          <div className="services-floating-particle p1"></div>
          <div className="services-floating-particle p2"></div>
          <div className="services-floating-particle p3"></div>
          <div className="services-floating-particle p4"></div>
          <div className="services-floating-particle p5"></div>
        </div>
        
        <div className="services-wave"></div>
      </section>

      {/* Services Overview Section */}
      <section className="services-overview">
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <div className="section-subtitle">What We Offer</div>
            <h2>Our Expertise</h2>
            <p>Comprehensive solutions tailored to your needs</p>
          </div>

          {/* Enhanced Floating SVG Elements */}
          <div className="floating-svg-container">
            <svg className="floating-svg dot-1" width="20" height="20" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="8" fill="#2c3c54" fillOpacity="0.15">
                <animate 
                  attributeName="fill-opacity" 
                  values="0.15;0.3;0.15" 
                  dur="4s" 
                  repeatCount="indefinite" />
              </circle>
              <animateTransform 
                attributeName="transform"
                type="translate"
                values="0 0; 0 -15; 0 0"
                dur="3s"
                repeatCount="indefinite" />
            </svg>
            
            <svg className="floating-svg dot-2" width="30" height="30" viewBox="0 0 30 30">
              <circle cx="15" cy="15" r="12" fill="#2c3c54" fillOpacity="0.18">
                <animate 
                  attributeName="fill-opacity" 
                  values="0.18;0.3;0.18" 
                  dur="5s" 
                  repeatCount="indefinite" />
              </circle>
              <animateTransform 
                attributeName="transform"
                type="translate"
                values="0 0; 15 0; 0 0"
                dur="4s"
                repeatCount="indefinite" />
            </svg>
            
            <svg className="floating-svg dot-3" width="15" height="15" viewBox="0 0 15 15">
              <circle cx="7.5" cy="7.5" r="6" fill="#942c24" fillOpacity="0.16">
                <animate 
                  attributeName="fill-opacity" 
                  values="0.16;0.28;0.16" 
                  dur="3.5s" 
                  repeatCount="indefinite" />
              </circle>
              <animateTransform 
                attributeName="transform"
                type="translate"
                values="0 0; 0 10; 0 0"
                dur="2.5s"
                repeatCount="indefinite" />
            </svg>
            
            <svg className="floating-svg dot-4" width="25" height="25" viewBox="0 0 25 25">
              <circle cx="12.5" cy="12.5" r="10" fill="#942c24" fillOpacity="0.15">
                <animate 
                  attributeName="fill-opacity" 
                  values="0.15;0.25;0.15" 
                  dur="4.5s" 
                  repeatCount="indefinite" />
              </circle>
              <animateTransform 
                attributeName="transform"
                type="translate"
                values="0 0; -12 0; 0 0"
                dur="3.5s"
                repeatCount="indefinite" />
            </svg>
            
            <svg className="floating-svg dot-5" width="18" height="18" viewBox="0 0 18 18">
              <circle cx="9" cy="9" r="7" fill="#2c3c54" fillOpacity="0.17">
                <animate 
                  attributeName="fill-opacity" 
                  values="0.17;0.3;0.17" 
                  dur="3.8s" 
                  repeatCount="indefinite" />
              </circle>
              <animateTransform 
                attributeName="transform"
                type="translate"
                values="0 0; 0 -10; 0 0"
                dur="2.8s"
                repeatCount="indefinite" />
            </svg>
            
            {/* New animated elements */}
            <svg className="floating-svg dot-6" width="35" height="35" viewBox="0 0 35 35">
              <circle cx="17.5" cy="17.5" r="15" stroke="#2c3c54" strokeWidth="1" fill="none" strokeOpacity="0.15">
                <animate 
                  attributeName="stroke-opacity" 
                  values="0.15;0.25;0.15" 
                  dur="4s" 
                  repeatCount="indefinite" />
                <animate 
                  attributeName="r" 
                  values="15;17;15" 
                  dur="4s" 
                  repeatCount="indefinite" />
              </circle>
              <animateTransform 
                attributeName="transform"
                type="translate"
                values="0 0; 10 10; 0 0"
                dur="6s"
                repeatCount="indefinite" />
            </svg>
            
            <svg className="floating-svg dot-7" width="40" height="40" viewBox="0 0 40 40">
              <polygon points="20,5 25,15 35,20 25,25 20,35 15,25 5,20 15,15" fill="#942c24" fillOpacity="0.08">
                <animate 
                  attributeName="fill-opacity" 
                  values="0.08;0.15;0.08" 
                  dur="5s" 
                  repeatCount="indefinite" />
              </polygon>
              <animateTransform 
                attributeName="transform"
                type="rotate"
                from="0 20 20"
                to="360 20 20"
                dur="20s"
                repeatCount="indefinite" />
            </svg>
          </div>

          <div className="services-grid" ref={serviceCardsRef}>
            {/* Service Card 1 */}
            <div className="service-item" data-aos="fade-up" data-aos-delay="100">
              <div className="service-icon">
                <FaDesktop />
              </div>
              <div className="service-content">
                <h3>Responsive Web Design</h3>
                <p>
                  We develop modern responsive websites, with packages for personal profiles, 
                  e-commerce, business websites, and website re-design and maintenance.
                </p>
                <div className="service-image">
                  <img src={webDesign} alt="Web Design" />
                </div>
                <a href="/contact" className="service-link">
                  Request Service <FaAngleRight />
                </a>
              </div>
              
              {/* Service card background SVG */}
              <div className="service-card-bg">
                <svg viewBox="0 0 200 200" width="200" height="200">
                  <path d="M30,10 Q50,5 70,10 T100,20 T130,10 T160,20 T180,30 V180 H20 V30 Q25,15 30,10" 
                    fill="none" stroke="#2c3c54" strokeWidth="0.5" strokeOpacity="0.1" />
                </svg>
              </div>
            </div>

            {/* Service Card 2 */}
            <div className="service-item" data-aos="fade-up" data-aos-delay="200">
              <div className="service-icon">
                <FaPencilRuler />
              </div>
              <div className="service-content">
                <h3>Graphic Design</h3>
                <p>
                  We create eye-catching graphics including logo designs, flyer designs, 
                  social media posts, software wireframes, and UI/UX mockups.
                </p>
                <div className="service-image">
                  <img src={graphicDesign} alt="Graphic Design" />
                </div>
                <a href="/contact" className="service-link">
                  Request Service <FaAngleRight />
                </a>
              </div>
              
              {/* Service card background SVG */}
              <div className="service-card-bg">
                <svg viewBox="0 0 200 200" width="200" height="200">
                  <path d="M30,10 Q50,5 70,10 T100,20 T130,10 T160,20 T180,30 V180 H20 V30 Q25,15 30,10" 
                    fill="none" stroke="#2c3c54" strokeWidth="0.5" strokeOpacity="0.1" />
                </svg>
              </div>
            </div>

            {/* Service Card 3 */}
            <div className="service-item" data-aos="fade-up" data-aos-delay="300">
              <div className="service-icon">
                <FaGamepad />
              </div>
              <div className="service-content">
                <h3>Game Development</h3>
                <p>
                  We develop engaging games across different genres, including educational games,
                  sports simulations, and action-packed experiences.
                </p>
                <div className="service-image">
                  <img src={gaming} alt="Game Development" />
                </div>
                <a href="/contact" className="service-link">
                  Request Service <FaAngleRight />
                </a>
              </div>
              
              {/* Service card background SVG */}
              <div className="service-card-bg">
                <svg viewBox="0 0 200 200" width="200" height="200">
                  <path d="M30,10 Q50,5 70,10 T100,20 T130,10 T160,20 T180,30 V180 H20 V30 Q25,15 30,10" 
                    fill="none" stroke="#2c3c54" strokeWidth="0.5" strokeOpacity="0.1" />
                </svg>
              </div>
            </div>

            {/* Service Card 4 */}
            <div className="service-item" data-aos="fade-up" data-aos-delay="400">
              <div className="service-icon">
                <FaMobile />
              </div>
              <div className="service-content">
                <h3>Mobile & Desktop Apps</h3>
                <p>
                  We develop cross-platform applications that are compatible with various operating systems,
                  including Android, iOS, Windows, macOS, and Linux.
                </p>
                <div className="service-image">
                  <img src={mobileDesktop} alt="Mobile and Desktop Apps" />
                </div>
                <a href="/contact" className="service-link">
                  Request Service <FaAngleRight />
                </a>
              </div>
              
              {/* Service card background SVG */}
              <div className="service-card-bg">
                <svg viewBox="0 0 200 200" width="200" height="200">
                  <path d="M30,10 Q50,5 70,10 T100,20 T130,10 T160,20 T180,30 V180 H20 V30 Q25,15 30,10" 
                    fill="none" stroke="#2c3c54" strokeWidth="0.5" strokeOpacity="0.1" />
                </svg>
              </div>
            </div>

            {/* Service Card 5 */}
            <div className="service-item" data-aos="fade-up" data-aos-delay="500">
              <div className="service-icon">
                <FaChalkboardTeacher />
              </div>
              <div className="service-content">
                <h3>Coding Tutorials</h3>
                <p>
                  We offer comprehensive programming courses at beginner, intermediate, and advanced levels
                  for various programming languages and frameworks.
                </p>
                <div className="service-image">
                  <img src={tutoring} alt="Coding Tutorials" />
                </div>
                <a href="/contact" className="service-link">
                  Request Service <FaAngleRight />
                </a>
              </div>
              
              {/* Service card background SVG */}
              <div className="service-card-bg">
                <svg viewBox="0 0 200 200" width="200" height="200">
                  <path d="M30,10 Q50,5 70,10 T100,20 T130,10 T160,20 T180,30 V180 H20 V30 Q25,15 30,10" 
                    fill="none" stroke="#2c3c54" strokeWidth="0.5" strokeOpacity="0.1" />
                </svg>
              </div>
            </div>

            {/* Service Card 6 */}
            <div className="service-item" data-aos="fade-up" data-aos-delay="600">
              <div className="service-icon">
                <FaCode />
              </div>
              <div className="service-content">
                <h3>Custom Solutions</h3>
                <p>
                  We provide tailored software solutions to address your specific business 
                  challenges and requirements, delivering premium open source solutions.
                </p>
                <div className="service-image">
                  <img src={others} alt="Custom Solutions" />
                </div>
                <a href="/contact" className="service-link">
                  Request Service <FaAngleRight />
                </a>
              </div>
              
              {/* Service card background SVG */}
              <div className="service-card-bg">
                <svg viewBox="0 0 200 200" width="200" height="200">
                  <path d="M30,10 Q50,5 70,10 T100,20 T130,10 T160,20 T180,30 V180 H20 V30 Q25,15 30,10" 
                    fill="none" stroke="#2c3c54" strokeWidth="0.5" strokeOpacity="0.1" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta" ref={ctaSectionRef}>
        <div className="container">
          <div className="services-cta-content">
            <h2>Ready to Bring Your Ideas to Life?</h2>
            <p>Contact us today for a free consultation and let's discuss how we can help your business grow.</p>
            <div className="cta-buttons">
              <a href="/contact" className="cta-button cta-primary">
                Get Started
              </a>
              <a href="/portfolio" className="cta-button cta-secondary">
                View Our Work
              </a>
            </div>
          </div>
        </div>
        
        {/* Dynamic Shapes for CTA */}
        <div className="cta-shapes">
          <div className="cta-shape cta-shape-1"></div>
          <div className="cta-shape cta-shape-2"></div>
          <div className="cta-shape cta-shape-3"></div>
          
          {/* Animated SVGs */}
          <svg className="cta-animated-svg svg-rotate-1" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#ffffff" opacity="0.03" d="M39.9,-48.1C51.1,-36,59.2,-22.6,64.1,-6.8C69,9,70.8,27.1,63.1,41.4C55.4,55.7,38.3,66.2,20.7,70.5C3.1,74.8,-15,72.9,-28.9,64.8C-42.8,56.7,-52.4,42.5,-60.7,26.5C-69,10.5,-75.9,-7.2,-71.3,-21.2C-66.8,-35.2,-50.7,-45.5,-35.4,-55.7C-20.1,-65.9,-5.8,-76,7.6,-83.9C21,-91.8,42.1,-97.6,51.7,-84.9C61.4,-72.2,59.7,-41.1,39.9,-48.1Z" transform="translate(100 100)">
              <animateTransform 
                attributeName="transform" 
                type="rotate"
                from="0 100 100"
                to="360 100 100" 
                dur="120s" 
                repeatCount="indefinite" />
            </path>
          </svg>
          
          <svg className="cta-animated-svg svg-rotate-2" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#ffffff" opacity="0.03" d="M43.7,-50.2C58.3,-36.9,73,-23.9,76.5,-8.2C80,7.5,72.3,26,61.5,44.7C50.7,63.5,36.8,82.6,19.7,87.4C2.5,92.2,-17.8,82.7,-36.8,70.4C-55.8,58.1,-73.4,42.9,-79.4,24.5C-85.4,6.1,-79.7,-15.5,-68.4,-32.2C-57.1,-48.9,-40.2,-60.8,-23.8,-73.1C-7.3,-85.4,8.6,-98.1,23.1,-94.4C37.7,-90.7,50.8,-70.6,43.7,-50.2Z" transform="translate(100 100)">
              <animateTransform 
                attributeName="transform" 
                type="rotate"
                from="360 100 100"
                to="0 100 100" 
                dur="90s" 
                repeatCount="indefinite" />
            </path>
          </svg>
          
          {/* New SVG Pulse Element */}
          <svg className="cta-animated-svg svg-pulse" width="300" height="300" viewBox="0 0 300 300">
            <circle cx="150" cy="150" r="100" fill="none" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.1">
              <animate 
                attributeName="r" 
                values="100;120;100" 
                dur="4s" 
                repeatCount="indefinite" />
              <animate 
                attributeName="stroke-opacity" 
                values="0.1;0.3;0.1" 
                dur="4s" 
                repeatCount="indefinite" />
            </circle>
            <circle cx="150" cy="150" r="80" fill="none" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.1">
              <animate 
                attributeName="r" 
                values="80;100;80" 
                dur="4s" 
                begin="0.5s"
                repeatCount="indefinite" />
            </circle>
            <circle cx="150" cy="150" r="60" fill="none" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.1">
              <animate 
                attributeName="r" 
                values="60;80;60" 
                dur="4s" 
                begin="1s"
                repeatCount="indefinite" />
            </circle>
          </svg>
          
          {/* Floating Particles */}
          <div className="cta-floating-particles">
            <div className="cta-particle p1"></div>
            <div className="cta-particle p2"></div>
            <div className="cta-particle p3"></div>
            <div className="cta-particle p4"></div>
            <div className="cta-particle p5"></div>
            
            {/* Additional particles */}
            <div className="cta-particle p6"></div>
            <div className="cta-particle p7"></div>
            <div className="cta-particle p8"></div>
          </div>
        </div>
      </section>
    </div>
  );
}
